/**
 * `/tutorial` (index) と `/tutorial/:game` のエントリ。
 *
 * 実装済みの game だけ React.lazy で動的 import される (初回アクセス時に
 * 専用 WASM + UI chunk が fetch される)。 未実装の game は index で
 * 「準備中」 表示。
 */

import { lazy, Suspense } from 'react';
import { LoadingScreen } from '../components/Loading';
import { GAME_ORDER, gameIconSlug, gameLabel } from '../lib/game-meta';
import { onLinkClick } from '../lib/navigate';
import type { GameId } from '../wasm';

const ChessTutorial = lazy(() => import('./ChessTutorial'));
const ReversiTutorial = lazy(() => import('./ReversiTutorial'));
const RenjuTutorial = lazy(() => import('./RenjuTutorial'));
const AnimalShogiTutorial = lazy(() => import('./AnimalShogiTutorial'));
const BackgammonTutorial = lazy(() => import('./BackgammonTutorial'));
const ShogiTutorial = lazy(() => import('./ShogiTutorial'));
const JungoTutorial = lazy(() => import('./JungoTutorial'));
const Connect6Tutorial = lazy(() => import('./Connect6Tutorial'));
const TwixtTutorial = lazy(() => import('./TwixtTutorial'));
const XiangqiTutorial = lazy(() => import('./XiangqiTutorial'));
const JanggiTutorial = lazy(() => import('./JanggiTutorial'));
const DraughtsTutorial = lazy(() => import('./DraughtsTutorial'));
const KalahTutorial = lazy(() => import('./KalahTutorial'));
const HeadsUpPokerTutorial = lazy(() => import('./HeadsUpPokerTutorial'));

interface GameCard {
  id: string;
  label: string;
  description: string;
  ready: boolean;
}

const iconOf = (id: string) => `/icons/games/${id}.svg`;

// 並び・表示名はゲーム名の正本 (lib/game-meta) の GAME_ORDER / label。 id は URL と
// アイコンの slug。 ルールの補足 (連珠ルール / 純碁 / トライルール / 国際ルール) は
// 名前に括弧で足さず description に書く (= 他画面と同じ名前で出す)。
const DESCRIPTIONS: Partial<Record<GameId, string>> = {
  Renju: '5 連を先に作った方が勝ち。 黒に禁手がある連珠ルール',
  AnimalShogi: '3 × 4 の小盤で遊ぶ将棋入門',
  Reversi: '挟んで裏返し、石数で競う',
  Chess: 'キングを詰ませる王道盤上戦',
  Jungo: '地ではなく石数で勝つ純碁ルール',
  Shogi: '取った駒を打ち合う詰みゲーム。 トライルールあり',
  Backgammon: 'ダイスで進める駒移動レース',
  Connect6: '19 路に黒先 1 手 → 以降は 2 石ずつ。 6 連で勝ち',
  TwixT: 'ペグを置き knight-move で自動リンク、 2 辺を繋ぐ',
  Xiangqi: '9 × 10 盤、 河と九宮を持つ中国将棋',
  Janggi: '9 × 10 盤の朝鮮将棋、 点数制決着あり',
  Draughts: '10 × 10 の国際ルール、 強制取り + ダム昇格',
  Kalah: '6 pit と 2 ストア、 free turn と capture で稼ぐ',
  HeadsUpPoker: '手札 2 枚 + 共有 5 枚で役を作る 1 対 1 ポーカー',
};
const GAMES: GameCard[] = GAME_ORDER.map((g) => ({
  id: gameIconSlug(g),
  label: gameLabel(g),
  description: DESCRIPTIONS[g] ?? 'CPU 対戦で遊べます',
  ready: DESCRIPTIONS[g] !== undefined,
}));

interface Props {
  game: string | null;
}

export function Tutorial({ game }: Props) {
  if (game === null) {
    return <TutorialIndex />;
  }
  if (game === 'chess') {
    return (
      <Suspense fallback={<LoadingScreen label="チュートリアルを読み込み中" />}>
        <ChessTutorial />
      </Suspense>
    );
  }
  if (game === 'reversi') {
    return (
      <Suspense fallback={<LoadingScreen label="チュートリアルを読み込み中" />}>
        <ReversiTutorial />
      </Suspense>
    );
  }
  if (game === 'renju') {
    return (
      <Suspense fallback={<LoadingScreen label="チュートリアルを読み込み中" />}>
        <RenjuTutorial />
      </Suspense>
    );
  }
  if (game === 'animal-shogi') {
    return (
      <Suspense fallback={<LoadingScreen label="チュートリアルを読み込み中" />}>
        <AnimalShogiTutorial />
      </Suspense>
    );
  }
  if (game === 'backgammon') {
    return (
      <Suspense fallback={<LoadingScreen label="チュートリアルを読み込み中" />}>
        <BackgammonTutorial />
      </Suspense>
    );
  }
  if (game === 'shogi') {
    return (
      <Suspense fallback={<LoadingScreen label="チュートリアルを読み込み中" />}>
        <ShogiTutorial />
      </Suspense>
    );
  }
  if (game === 'jungo') {
    return (
      <Suspense fallback={<LoadingScreen label="チュートリアルを読み込み中" />}>
        <JungoTutorial />
      </Suspense>
    );
  }
  if (game === 'connect6') {
    return (
      <Suspense fallback={<LoadingScreen label="チュートリアルを読み込み中" />}>
        <Connect6Tutorial />
      </Suspense>
    );
  }
  if (game === 'twixt') {
    return (
      <Suspense fallback={<LoadingScreen label="チュートリアルを読み込み中" />}>
        <TwixtTutorial />
      </Suspense>
    );
  }
  if (game === 'xiangqi') {
    return (
      <Suspense fallback={<LoadingScreen label="チュートリアルを読み込み中" />}>
        <XiangqiTutorial />
      </Suspense>
    );
  }
  if (game === 'janggi') {
    return (
      <Suspense fallback={<LoadingScreen label="チュートリアルを読み込み中" />}>
        <JanggiTutorial />
      </Suspense>
    );
  }
  if (game === 'draughts') {
    return (
      <Suspense fallback={<LoadingScreen label="チュートリアルを読み込み中" />}>
        <DraughtsTutorial />
      </Suspense>
    );
  }
  if (game === 'kalah') {
    return (
      <Suspense fallback={<LoadingScreen label="チュートリアルを読み込み中" />}>
        <KalahTutorial />
      </Suspense>
    );
  }
  if (game === 'heads-up-poker') {
    return (
      <Suspense fallback={<LoadingScreen label="チュートリアルを読み込み中" />}>
        <HeadsUpPokerTutorial />
      </Suspense>
    );
  }
  return (
    <main className="centered">
      <p>「{game}」 のチュートリアルは準備中です。</p>
      <a href="/tutorial" onClick={onLinkClick('/tutorial')}>← チュートリアル一覧へ</a>
    </main>
  );
}

function TutorialIndex() {
  return (
    <main className="page tutorial-page tutorial-index-page">
      <nav className="tutorial-top">
        <a href="/" className="ghost" onClick={onLinkClick('/')}>← ホーム</a>
        <span className="tutorial-game-name">チュートリアル一覧</span>
      </nav>
      <h2 className="tutorial-title">学べるゲーム</h2>
      <p className="tutorial-index-lead">
        各ゲームの基本ルールを、 実際に盤を触りながら学べます。 1 ステップずつ進める形式で、
        所要時間 はそれぞれ 5–10 分ほど。
      </p>
      <ul className="tutorial-index-grid">
        {GAMES.map((g) => (
          <li key={g.id}>
            {g.ready ? (
              <a
                className="tutorial-index-card"
                href={`/tutorial/${g.id}`}
                onClick={onLinkClick(`/tutorial/${g.id}`)}
              >
                <img className="tutorial-index-card-icon" src={iconOf(g.id)} alt="" width={48} height={48} />
                <div className="tutorial-index-card-body">
                  <div className="tutorial-index-card-title">{g.label}</div>
                  <div className="tutorial-index-card-desc">{g.description}</div>
                </div>
              </a>
            ) : (
              <div className="tutorial-index-card disabled">
                <img className="tutorial-index-card-icon" src={iconOf(g.id)} alt="" width={48} height={48} />
                <div className="tutorial-index-card-body">
                  <div className="tutorial-index-card-title">{g.label}</div>
                  <div className="tutorial-index-card-desc">{g.description} (準備中)</div>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </main>
  );
}
